import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import { MaskedInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useState } from "react";
import type { Control, FieldErrors, Path } from "react-hook-form";

import { ControlledInput } from "../../../../../components/Card/ControlledInput";
import { validateProbabilityPercentage } from "../utils/allocation-validation";

type BaseRowProps = {
  total_cost: number;
  probability_of_success: number | null;
};

type TotalWithProbabilityProps<T extends BaseRowProps> = {
  control: Control<T>;
  errors: FieldErrors<T>;
  handleFieldSave: (
    field: string,
    value: string | number | boolean,
  ) => Promise<void>;
  probability: number | null;
  name?: Path<T>;
};

const getProbabilityColor = (prob: number | null) => {
  if (!prob) return "grey";
  if (prob >= 0.75) return "green";
  if (prob >= 0.5) return "yellow";
  if (prob >= 0.25) return "orange";
  return "red";
};

export const TotalWithProbability = <T extends BaseRowProps>({
  control,
  errors,
  handleFieldSave,
  probability,
  name = "total_cost" as Path<T>,
}: TotalWithProbabilityProps<T>) => {
  const [showProbabilityModal, setShowProbabilityModal] = useState(false);
  const [probabilityValue, setProbabilityValue] = useState<string>(
    probability ? Math.round(probability * 100).toString() : "0",
  );

  const handleSaveProbability = async () => {
    const numValue = parseFloat(probabilityValue);

    // validate using zod schema
    const validationError = validateProbabilityPercentage(numValue);
    if (validationError) {
      KaboodleToaster.error(validationError);
      return;
    }

    // convert percentage (0-100) to decimal (0-1) for backend
    await handleFieldSave("probability_of_success", numValue / 100);
    setShowProbabilityModal(false);
  };

  return (
    <>
      <div className="KaboodleMaskedInput--Probability">
        <div className="input-wrapper">
          <ControlledInput
            control={control}
            name={name}
            mask="total_dollar"
            errors={errors}
            readOnly={true}
            onSave={(value) => handleFieldSave(name as string, value)}
            componentType={MaskedInput}
          />
          <span
            className={`percentage ${getProbabilityColor(probability)} clickable`}
            onClick={() => setShowProbabilityModal(true)}
            style={{ cursor: "pointer" }}
          >
            {probability ? Math.round(probability * 100) : 0}%
          </span>
        </div>
      </div>

      <KaboodleModal
        isOpen={showProbabilityModal}
        onClose={() => setShowProbabilityModal(false)}
        footer={
          <div className="flex jc-c">
            <Button onClick={handleSaveProbability}>Save</Button>
            <Button onClick={() => setShowProbabilityModal(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        <Card heading="Probability of Success">
          <div className="probability-input-modal">
            <MaskedInput
              name="probability-input"
              value={probabilityValue}
              onChange={(e: { target: { value: string } }) =>
                setProbabilityValue(e.target.value)
              }
              mask="number"
              maxLength={3}
              placeholder="Enter probability (0-100)"
              errors={{}}
            />
            <span className="percentage-label" style={{ marginLeft: "10px" }}>
              %
            </span>
          </div>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default TotalWithProbability;
